import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, ShieldBan, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CALL_OUTCOMES, dt, duration, money, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

function AudioPlayer({ src, length, audioRef, position, onPosition }) {
  const [playing, setPlaying] = useState(false);

  if (!src) {
    return <p className="px-4 py-4 text-sm text-slate-500" data-testid="recording-unavailable">No recording available for this call.</p>;
  }

  const total = audioRef.current?.duration || length || 0;

  return (
    <div data-testid="call-audio-player" className="flex items-center gap-3 px-4 py-4">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        data-testid="recording-audio-element"
        onTimeUpdate={(e) => onPosition(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <Button
        size="icon"
        data-testid="recording-play-button"
        className="h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700"
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (el.paused) el.play();
          else el.pause();
        }}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </Button>
      <div className="flex-1">
        <Progress
          value={total ? Math.min(100, (position / total) * 100) : 0}
          className="h-1.5 cursor-pointer"
          data-testid="recording-progress"
          onClick={(e) => {
            const el = audioRef.current;
            if (!el || !total) return;
            const rect = e.currentTarget.getBoundingClientRect();
            el.currentTime = ((e.clientX - rect.left) / rect.width) * total;
          }}
        />
        <div className="mt-1 flex justify-between font-mono text-[11px] text-slate-500">
          <span data-testid="recording-position">{duration(Math.floor(position))}</span>
          <span>{duration(Math.floor(total))}</span>
        </div>
      </div>
      <span className="text-xs text-slate-500">Provider recording</span>
    </div>
  );
}

function QualificationSteps({ stages, lead, outcome }) {
  if (!lead) {
    return (
      <p className="px-4 py-4 text-sm text-slate-500" data-testid="qualification-empty">
        No lead was created from this call{outcome ? ` (outcome: ${title(outcome)})` : ""}.
      </p>
    );
  }
  const list = stages || [];
  const current = list.indexOf(lead.pipeline_stage);
  const reached = new Set((lead.status_history || []).map((h) => h.to_status));
  const lost = lead.pipeline_stage === "lost";

  return (
    <div className="space-y-3 p-4" data-testid="qualification-steps">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatusBadge value={lead.interest_level || "unknown"} testId="lead-interest-level" />
        <ScoreBadge score={lead.interest_score} testId="lead-interest-score" />
        <Link to={`/leads/${lead.id}`} className="text-blue-600 hover:underline" data-testid="open-lead-link">
          Open lead →
        </Link>
      </div>
      <ol className="space-y-1.5">
        {list.map((stage, index) => {
          const done = reached.has(stage) || (current >= 0 && index < current);
          const active = stage === lead.pipeline_stage;
          return (
            <li key={stage} className="flex items-center gap-2 text-sm" data-testid={`qualification-step-${stage}`}>
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
                  active
                    ? lost
                      ? "bg-rose-600 text-white"
                      : "bg-blue-600 text-white"
                    : done
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-400"
                )}
              >
                {done && !active ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              <span className={cn(active ? "font-semibold text-slate-900" : done ? "text-slate-700" : "text-slate-400")}>
                {title(stage)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function CallDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [correction, setCorrection] = useState({ outcome: "", interest_score: "", note: "" });
  const [callbackAt, setCallbackAt] = useState("");
  const audioRef = useRef(null);
  const [position, setPosition] = useState(0);
  const activeRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["call", id],
    queryFn: () => get(`/calls/${id}`),
    refetchInterval: (query) =>
      ["queued", "dialing", "ringing", "active"].includes(query.state.data?.status) ? 3000 : false,
  });

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [position]);

  const correct = useMutation({
    mutationFn: (payload) => api.post(`/calls/${id}/correct`, payload),
    onSuccess: () => {
      toast.success("Call updated. The original AI result is preserved and the change is audited.");
      setCorrection({ outcome: "", interest_score: "", note: "" });
      queryClient.invalidateQueries({ queryKey: ["call", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const createLead = useMutation({
    mutationFn: () => api.post(`/leads/from-call/${id}`, {}),
    onSuccess: () => toast.success("Lead created from this call"),
    onError: (e) => toast.error(apiError(e)),
  });

  const createCallback = useMutation({
    mutationFn: () => api.post(`/callbacks/from-call/${id}`, { scheduled_at: new Date(callbackAt).toISOString() }),
    onSuccess: () => {
      toast.success("Callback scheduled");
      setCallbackAt("");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const addDnc = useMutation({
    mutationFn: () => api.post(`/calls/${id}/dnc`, { reason: "Added from call review" }),
    onSuccess: () => {
      toast.success("Number added to the global do-not-call registry");
      queryClient.invalidateQueries({ queryKey: ["call", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading call…</p>;
  const result = data.result || {};
  const segments = data.transcript || [];
  const activeIndex = segments.reduce(
    (acc, seg, i) => ((seg.offset_seconds || 0) <= position ? i : acc),
    -1
  );
  const recordingSrc = data.recording_url || data.recording?.url || null;
  const seek = (seconds) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, seconds);
    setPosition(Math.max(0, seconds));
    el.play().catch(() => {});
  };

  return (
    <>
      <PageHeader
        testId="call-detail-page"
        title={data.contact_name || data.phone}
        subtitle={`${data.campaign_name} · ${data.agent_name} v${data.agent_version} · ${(data.language || "").toUpperCase()} · ${data.country}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={data.status} testId="call-status-badge" />
            {data.outcome ? <StatusBadge value={data.outcome} testId="call-outcome-badge" /> : null}
            <ScoreBadge score={data.interest_score} testId="call-score-badge" />
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        {[
          ["Started", dt(data.started_at)],
          ["Duration", duration(data.duration_seconds)],
          ["Telephony cost", money(data.telephony_cost)],
          ["AI cost", money(data.ai_cost)],
          ["Total cost", money(data.total_cost)],
          ["Latency", `${data.latency_ms || 0} ms`],
          ["Provider", title(data.provider)],
          ["Provider call id", data.provider_call_id],
          ["From number", data.from_number],
          ["Ended reason", title(data.ended_reason || "—")],
          ["Quality score", data.quality_score ? `${data.quality_score}/100` : "—"],
          ["Attempt", data.attempt],
        ].map(([label, value]) => (
          <div key={label} className="panel px-3 py-2">
            <p className="kpi-label">{label}</p>
            <p className="truncate text-sm font-medium text-slate-800">{value ?? "—"}</p>
          </div>
        ))}
      </div>

      {(data.compliance_flags || []).length ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="compliance-flags">
          Compliance flags: {data.compliance_flags.map((f) => title(f)).join(", ")}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Section title="Recording" subtitle="Play the real provider recording — the transcript follows the timeline">
            <AudioPlayer
              src={recordingSrc}
              length={data.duration_seconds || 0}
              audioRef={audioRef}
              position={position}
              onPosition={setPosition}
            />
          </Section>

          <Section title="Transcript" subtitle="Click any line to jump the recording to that moment"
            actions={data.transcript_source ? (
              <span data-testid="call-transcript-source" className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                Source: {data.transcript_source}
              </span>
            ) : null}>
            <div className="max-h-[28rem] space-y-2.5 overflow-y-auto p-4" data-testid="call-transcript">
              {segments.length === 0 ? (
                <p className="text-sm text-slate-500">No transcript returned by the provider yet.</p>
              ) : (
                segments.map((seg, index) => {
                  const active = index === activeIndex;
                  return (
                    <div
                      key={seg.id}
                      ref={active ? activeRef : null}
                      data-testid={`transcript-segment-${index}`}
                      data-active={active ? "true" : "false"}
                      onClick={() => seek(seg.offset_seconds || 0)}
                      className={cn(
                        "flex cursor-pointer flex-col gap-1 rounded-lg px-1 py-0.5 transition-colors",
                        seg.speaker === "customer" ? "items-end" : "items-start",
                        active ? "bg-amber-50" : "hover:bg-slate-50"
                      )}
                    >
                      <span className="font-mono text-[10px] text-slate-400">
                        {duration(seg.offset_seconds)} · {seg.speaker === "customer" ? "Customer" : "AI agent"}
                      </span>
                      <p
                        className={cn(
                          "max-w-[80%] rounded-lg px-3 py-2 text-sm transition-all",
                          seg.speaker === "customer"
                            ? "bg-slate-100 text-slate-700"
                            : "bg-blue-50 text-blue-900 ring-1 ring-blue-100",
                          active ? "ring-2 ring-amber-400" : ""
                        )}
                      >
                        {seg.text}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </Section>

          <Section title="Event timeline">
            <ul className="divide-y divide-slate-100" data-testid="call-events">
              {(data.events || []).map((event) => (
                <li key={event.id} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="font-mono text-xs text-slate-700">{event.event}</span>
                  <span className="text-xs text-slate-500">{dt(event.created_at)}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Lead qualification steps" subtitle="Where this conversation moved the lead">
            <QualificationSteps stages={data.pipeline_stages} lead={data.lead} outcome={data.outcome} />
          </Section>

          {(data.quality_breakdown || []).length ? (
            <Section title={`Quality score · ${data.quality_score ?? "—"}/100`} subtitle="Computed from the real transcript">
              <ul className="divide-y divide-slate-100" data-testid="quality-breakdown">
                {data.quality_breakdown.map((item) => (
                  <li key={item.key} className="px-4 py-2" data-testid={`quality-item-${item.key}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-800">{item.label}</span>
                      <span className="font-mono text-xs text-slate-600">{item.points}/{item.max_points}</span>
                    </div>
                    <p className="text-[11px] text-slate-500">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="AI summary">
            <div className="space-y-3 p-4 text-sm">
              <p className="text-slate-700" data-testid="call-summary">{result.summary || "No summary returned by the provider."}</p>
              <div>
                <p className="kpi-label">Objections detected in the transcript</p>
                {(result.objection_details || []).length ? (
                  <ul className="mt-1 space-y-1.5" data-testid="call-objections">
                    {result.objection_details.map((o) => (
                      <li key={o.key} className="rounded-md bg-slate-50 px-2 py-1.5" data-testid={`objection-${o.key}`}>
                        <button
                          type="button"
                          className="text-left text-sm font-medium text-slate-800 hover:text-blue-600"
                          onClick={() => seek(o.offset_seconds || 0)}
                        >
                          {o.label} · <span className="font-mono text-[11px] text-slate-500">{duration(o.offset_seconds)}</span>
                        </button>
                        <p className="text-xs italic text-slate-600">“{o.quote}”</p>
                        <p className={cn("text-[11px]", o.handled ? "text-emerald-600" : "text-amber-600")}>
                          {o.handled ? "Answered by the agent" : "Not answered by the agent"}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-700" data-testid="call-objections">
                    {(result.objections || []).map(title).join(", ") || "No objection detected in the transcript"}
                  </p>
                )}
              </div>
              <div>
                <p className="kpi-label">Customer questions</p>
                {(result.question_details || []).length ? (
                  <ul className="mt-1 space-y-1 text-sm text-slate-700" data-testid="call-questions">
                    {result.question_details.map((q, i) => (
                      <li key={i}>
                        <button type="button" className="text-left hover:text-blue-600" onClick={() => seek(q.offset_seconds || 0)}>
                          <span className="font-mono text-[11px] text-slate-500">{duration(q.offset_seconds)}</span> · {q.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-700" data-testid="call-questions">{(result.questions || []).join(" · ") || "No question detected in the transcript"}</p>
                )}
              </div>
              <div>
                <p className="kpi-label">Products mentioned</p>
                <p className="text-slate-700">{(result.products_mentioned || []).join(", ") || "—"}</p>
              </div>
              <div>
                <p className="kpi-label">Callback requested</p>
                <p className="text-slate-700">{result.callback_requested ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="kpi-label">Suggested human follow-up</p>
                <p className="text-slate-700">{result.suggested_followup || "—"}</p>
              </div>
              {(result.manual_corrections || []).length ? (
                <div>
                  <p className="kpi-label">Manual corrections</p>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {result.manual_corrections.map((c, i) => (
                      <li key={i}>
                        {c.user_email} · {dt(c.at)}
                        {c.outcome ? ` · outcome ${c.outcome.from} → ${c.outcome.to}` : ""}
                        {c.interest_score ? ` · score ${c.interest_score.from} → ${c.interest_score.to}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Original AI result: {title(result.ai_outcome)}
                    {result.ai_interest_score != null ? ` · ${result.ai_interest_score}/10` : ""}
                  </p>
                </div>
              ) : null}
            </div>
          </Section>

          {can("calls", "write") ? (
            <Section title="Review actions">
              <div className="space-y-3 p-4">
                <div className="space-y-1.5">
                  <Label>Correct outcome</Label>
                  <Select value={correction.outcome} onValueChange={(v) => setCorrection((c) => ({ ...c, outcome: v }))}>
                    <SelectTrigger data-testid="correct-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {CALL_OUTCOMES.map((o) => <SelectItem key={o} value={o}>{title(o)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Correct interest score (0–10)</Label>
                  <Input
                    data-testid="correct-score-input"
                    type="number"
                    min="0"
                    max="10"
                    value={correction.interest_score}
                    onChange={(e) => setCorrection((c) => ({ ...c, interest_score: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Internal note</Label>
                  <Textarea
                    data-testid="correct-note-input"
                    rows={2}
                    value={correction.note}
                    onChange={(e) => setCorrection((c) => ({ ...c, note: e.target.value }))}
                  />
                </div>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={!correction.outcome && !correction.interest_score && !correction.note}
                  onClick={() =>
                    correct.mutate({
                      outcome: correction.outcome || undefined,
                      interest_score: correction.interest_score === "" ? undefined : Number(correction.interest_score),
                      note: correction.note || undefined,
                    })
                  }
                  data-testid="correct-submit"
                >
                  Save correction
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => correct.mutate({ mark_for_review: true })}
                  data-testid="mark-review-button"
                >
                  Mark for quality review
                </Button>
                <Button variant="outline" className="w-full" onClick={() => createLead.mutate()} data-testid="create-lead-button">
                  Create lead
                </Button>
                <div className="space-y-1.5">
                  <Label>Create callback</Label>
                  <Input
                    data-testid="callback-datetime-input"
                    type="datetime-local"
                    value={callbackAt}
                    onChange={(e) => setCallbackAt(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={!callbackAt}
                    onClick={() => createCallback.mutate()}
                    data-testid="create-callback-button"
                  >
                    Schedule callback
                  </Button>
                </div>
                <Button
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  onClick={() => addDnc.mutate()}
                  data-testid="add-dnc-button"
                >
                  <ShieldBan className="mr-1 h-4 w-4" /> Add to do-not-call
                </Button>
              </div>
            </Section>
          ) : null}

          {(data.internal_notes || []).length ? (
            <Section title="Internal notes">
              <ul className="divide-y divide-slate-100" data-testid="call-notes">
                {data.internal_notes.map((note) => (
                  <li key={note.id} className="px-4 py-2 text-sm">
                    <p className="text-slate-700">{note.text}</p>
                    <p className="text-[11px] text-slate-400">{note.author} · {dt(note.created_at)}</p>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>
      </div>

      <Link to="/calls" className="text-sm text-blue-600 hover:underline" data-testid="back-to-calls">
        ← Back to calls
      </Link>
    </>
  );
}